import { DateRangePicker } from "@/components/molecule/dateRangePicker/DateRangePicker";
import { COMMON_TEXT } from "@/constants/text";
import { useModal } from "@/context/ModalStackManager";
import { ActionIcon, Flex, Text, TextProps } from "@mantine/core";
import { IconCalendar } from "@tabler/icons-react";
import dayjs from "dayjs";

type CalendarMode = "single" | "range";

type RangeValue = [Date | null, Date | null];
type SingleValue = Date | null;

type RangeProps = TextProps & {
  mode?: Extract<CalendarMode, "range">; // 기본값 range
  value: RangeValue; // 기존 dates 대체
  onChange: (v: RangeValue) => void; // 기존 setDates 대체
};

type SingleProps = TextProps & {
  mode: Extract<CalendarMode, "single">;
  value: SingleValue;
  onChange: (v: SingleValue) => void;
};

type CalendarTriggerProps = RangeProps | SingleProps;

const fmt = (d: Date | null | undefined, f = "YYYY-MM-DD") =>
  d ? dayjs(d).format(f) : "—";

export const CalendarTrigger = (props: CalendarTriggerProps) => {
  const { openModal } = useModal();

  const isSingle = props.mode === "single";

  const openCalendar = () => {
    // 단일/범위 모두 DateRangePicker 모달을 재사용
    // - single: 첫 번째 값만 사용
    // - range: 전체 값 사용
    const initialRange: RangeValue = isSingle
      ? [props.value as SingleValue, null]
      : (props.value as RangeValue);

    openModal(
      <DateRangePicker
        mode={isSingle ? "single" : "range"}
        dates={initialRange as [Date | null, Date | null]}
      />,
      null,
      COMMON_TEXT.FORM.CALENDAR,
      true
    ).then((result: [Date | null, Date | null] | unknown) => {
      if (!Array.isArray(result)) return;

      if (isSingle) {
        const [first] = result;
        (props as SingleProps).onChange(first ?? null);
      } else {
        (props as RangeProps).onChange([result[0] ?? null, result[1] ?? null]);
      }
    });
  };

  const { size } = props;

  return (
    <Flex gap="md" justify="center" align="center">
      {isSingle ? (
        <Text fw="bold" size={size ?? "xl"} c="white">
          {fmt((props as SingleProps).value)}
        </Text>
      ) : (
        <Text fw="bold" size={size ?? "xl"} c="white">
          {fmt((props as RangeProps).value[0])} ~{" "}
          {fmt((props as RangeProps).value[1])}
        </Text>
      )}

      <ActionIcon onClick={openCalendar}>
        <IconCalendar />
      </ActionIcon>
    </Flex>
  );
};
